import { logger } from 'common/services';
import { dataToFile } from 'common/utils';
import { format } from 'date-fns';

type StoredWsMessage = { path: string; payload: unknown; timestamp: number; type: 'INCOMING' | 'OUTGOING' };

export const MESSAGES_HISTORY_LIMIT = 1000;

/**
 * WS message paths whose payload carries long-lived secrets and must never reach centralized
 * logging (Datadog) or the local message dump. `chat_connection_details` carries the Ably token,
 * the rotating refresh credential, and the room encryptionKey (decrypts the persisted channel).
 */
const SECRET_PAYLOAD_PATH_SUFFIXES = ['chat_connection_details'];
const REDACTED_WS_PAYLOAD = '[redacted: secret WS payload]';

const redactSecretPayload = (path: string, payload: unknown): unknown =>
  SECRET_PAYLOAD_PATH_SUFFIXES.some((suffix) => path.endsWith(suffix)) ? REDACTED_WS_PAYLOAD : payload;

export const SocketMessagesLogger = () => {
  const messages: StoredWsMessage[] = [];

  const captureSocketMessage = (message: Omit<StoredWsMessage, 'timestamp'>) => {
    // Redact before it touches the in-memory history dump OR the Datadog log — the raw grant must
    // not persist anywhere in observability tooling. The real dispatch still uses the raw payload.
    const payload = redactSecretPayload(message.path, message.payload);

    messages.push({
      timestamp: Date.now(),
      type: message.type,
      path: message.path,
      payload,
    });
    if (messages.length > MESSAGES_HISTORY_LIMIT) {
      messages.shift();
    }

    logger.info('WebSocket message', {
      wsType: message.type,
      wsPath: message.path,
      wsPayload: payload,
    });
  };

  const dumpSocketMessages = () => {
    try {
      dataToFile(
        JSON.stringify(messages, null, 2),
        `ws_messages_log_${format(new Date(), 'dd_MM_yyyy_HH_mm_ss')}.txt`,
        'text/plain'
      );
      messages.length = 0;
    } catch (error) {
      logger.error('[socketMessagesLogger] Error while dumping WS messages', {
        error,
        extra: { messagesCount: messages.length },
      });
    }
  };

  return {
    captureSocketMessage,
    dumpSocketMessages,
  };
};

export const { captureSocketMessage, dumpSocketMessages } = SocketMessagesLogger();

// this makes it possible to call this function via e.g. dev tools console
window.dumpSocketMessages = dumpSocketMessages;
